import { FormFieldControl, Input } from "@components";
import { Button } from "@components/button/button-untitledui";
import { FileUpload } from "@components/file-upload";
import { Dataflow03, Lock01, ShieldPlus, XClose } from "@untitledui/icons";
import { useState } from "react";
import {
	CERTIFICATE_ACCEPT,
	CERTIFICATE_MAX_SIZE_BYTES,
} from "../constants/infraction-certificate.constant";
import type {
	CertificateFormErrors,
	CertificateFormValues,
} from "../types/infraction-certificate.types";
import { formatCnpj } from "../utils/formatters";

type InfractionCertificateConnectFormProps = {
	companyName: string | null;
	companyDocument: string | null;
	values: CertificateFormValues;
	errors: CertificateFormErrors;
	isSubmitting: boolean;
	onCancel: () => void;
	onClose: () => void;
	onChange: (values: Partial<CertificateFormValues>) => void;
	onSubmit: () => void;
};

export function InfractionCertificateConnectForm({
	companyName,
	companyDocument,
	values,
	errors,
	isSubmitting,
	onCancel,
	onClose,
	onChange,
	onSubmit,
}: InfractionCertificateConnectFormProps) {
	const [showPassword, setShowPassword] = useState(false);

	return (
		<div
			className="relative flex w-full flex-col"
			data-slot="infraction-certificate-connect-form"
		>
			<button
				aria-label="Fechar"
				className="absolute top-4 right-4 z-10 rounded-lg p-2 text-quaternary hover:bg-secondary_subtle"
				onClick={onClose}
				tabIndex={0}
				type="button"
			>
				<XClose className="size-5" />
			</button>
			<div className="border-border-secondary border-b px-8 pt-8 pb-4">
				<div className="flex items-center gap-3 rounded pr-8">
					<ShieldPlus
						aria-hidden
						className="size-6 bg-brand-25 text-brand-600"
					/>
					<h2 className="font-bold text-2xl text-primary">
						Conectar Certificado Digital
					</h2>
				</div>
				<div className="mt-1 mt-2 flex items-center gap-1.5 text-secondary text-sm">
					<Lock01 aria-hidden className="size-3.5" />
					<span>A conexão é segura e criptografada.</span>
				</div>
			</div>
			<div className="space-y-5 px-8 py-4">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<FormFieldControl htmlFor="company-name" label="Nome da Empresa">
						<Input disabled id="company-name" value={companyName ?? "—"} />
					</FormFieldControl>
					<FormFieldControl htmlFor="company-cnpj" label="CNPJ">
						<Input
							disabled
							id="company-cnpj"
							value={formatCnpj(companyDocument)}
						/>
					</FormFieldControl>
				</div>
				<FormFieldControl
					error={errors.certificate}
					htmlFor="certificate-upload"
					label="Certificado (e-CNPJ)"
				>
					<FileUpload.Root className="flex-1">
						<FileUpload.DropZone
							accept={CERTIFICATE_ACCEPT}
							hint="Envie apenas arquivos .PFX ou .P12"
							maxSize={CERTIFICATE_MAX_SIZE_BYTES}
							onDropFiles={(files) => {
								const file = files[0] ?? null;
								onChange({ certificateFile: file });
							}}
						/>
						{values.certificateFile ? (
							<p className="text-secondary text-sm">
								{values.certificateFile.name} (
								{Math.round(values.certificateFile.size / 1024)} KB)
							</p>
						) : null}
					</FileUpload.Root>
				</FormFieldControl>
				<FormFieldControl
					error={errors.certificatePassword}
					htmlFor="certificate-password"
					label="Senha do Certificado"
				>
					<Input
						error={!!errors.certificatePassword}
						id="certificate-password"
						onChange={(event) => {
							onChange({ certificatePassword: event.target.value });
						}}
						placeholder="Digite a senha do certificado"
						type={showPassword ? "text" : "password"}
						value={values.certificatePassword}
					/>
					<button
						aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
						className="mt-1 text-brand-600 text-xs underline"
						onClick={() => {
							setShowPassword((prev) => !prev);
						}}
						tabIndex={0}
						type="button"
					>
						{showPassword ? "Ocultar" : "Mostrar"} senha
					</button>
				</FormFieldControl>
			</div>
			<div className="flex flex-wrap items-center justify-center gap-3 border-border-secondary border-t px-8 py-6">
				<Button
					aria-label="Cancelar conexão do certificado"
					className="min-w-[140px] flex-1"
					color="secondary"
					onClick={onCancel}
					size="md"
					tabIndex={0}
				>
					Cancelar
				</Button>
				<Button
					aria-label="Validar e conectar"
					className="min-w-[200px] flex-1"
					color="primary"
					iconLeading={Dataflow03}
					isDisabled={isSubmitting}
					isLoading={isSubmitting}
					onClick={onSubmit}
					size="md"
					tabIndex={0}
				>
					Validar e Conectar
				</Button>
			</div>
		</div>
	);
}
